import type { ChannelCommand, CommandOption, CommandValue, Uuid } from "@shared/api";

/**
 * Команды ботов в поле ввода — как в Discord. Там параметры — плашки, у нас они
 * пишутся словом с двоеточием: «/roll sides:20 color:Синий». Подкоманды — словами
 * после имени: «/music play song:…». Команда с одной строкой может обойтись без
 * имени параметра: «/echo привет».
 *
 * Люди, каналы и роли приходят сюда разметкой плашек (`lib/pills`): «@max#0001»,
 * «<#id>», «<@&id>». Кого значит хэндл, говорит `people`.
 */

/** Типы параметров Discord. */
export const OPTION = {
  sub: 1,
  group: 2,
  text: 3,
  whole: 4,
  yesNo: 5,
  user: 6,
  channel: 7,
  role: 8,
  mentionable: 9,
  number: 10,
  file: 11,
} as const;

/** Что писать после «имя:» — в подсказке рядом с параметром. */
export function optionKind(option: CommandOption): string {
  if (option.choices?.length) return "вариант";
  switch (option.type) {
    case OPTION.whole:
      return "целое";
    case OPTION.number:
      return "число";
    case OPTION.yesNo:
      return "да или нет";
    case OPTION.user:
      return "@человек";
    case OPTION.channel:
      return "#канал";
    case OPTION.role:
      return "@роль";
    case OPTION.mentionable:
      return "@человек или роль";
    case OPTION.file:
      return "файл";
    default:
      return "текст";
  }
}

/** Набранная команда: сама она, путь подкоманд и параметры последнего уровня. */
export interface Typed {
  command: ChannelCommand;
  /** Подкоманды по порядку: группа и подкоманда, или одна подкоманда. */
  path: string[];
  /** Параметры уровня, до которого дошли; у команды с подкомандами без выбранной — сами подкоманды. */
  options: CommandOption[];
  /** Текст после имени и подкоманд. */
  rest: string;
}

const isSub = (option: CommandOption) => option.type === OPTION.sub || option.type === OPTION.group;

/**
 * Какая команда бота набрана: имя — целиком, дальше пробел или конец. `null` — не
 * команда бота (или имя ещё дописывают — тогда это подсказка «/»).
 */
export function typedCommand(text: string, commands: readonly ChannelCommand[]): Typed | null {
  const match = /^\/(\S+)(?:\s([\s\S]*))?$/.exec(text.trimStart());
  if (!match) return null;
  const command = commands.find((item) => item.type === 1 && item.name === match[1].toLowerCase());
  if (!command) return null;

  let options = command.options;
  let rest = match[2] ?? "";
  const path: string[] = [];
  // Не глубже двух слов: группа, потом подкоманда — как у Discord.
  for (let depth = 0; depth < 2 && options.some(isSub); depth++) {
    const word = /^\s*(\S+)(?:\s+([\s\S]*))?$/.exec(rest);
    const sub = word && options.find((option) => isSub(option) && option.name === word[1].toLowerCase());
    if (!sub) break;
    path.push(sub.name);
    options = sub.options ?? [];
    rest = word[2] ?? "";
  }
  return { command, path, options, rest };
}

/** Какой параметр сейчас набирают: последний «имя:» в тексте. */
export function currentOption(typed: Typed): CommandOption | null {
  let found: CommandOption | null = null;
  for (const match of typed.rest.matchAll(/(?:^|\s)([^\s:]+):/g)) {
    const option = typed.options.find((item) => item.name === match[1].toLowerCase());
    if (option) found = option;
  }
  return found;
}

/** Как человек и роль выглядят в разметке плашек. */
export interface Resolve {
  /** «@max#0001» без «@» — его id; `null` — такого не знаем. */
  person: (handle: string) => Uuid | null;
}

export type Parsed = { ok: true; options: CommandValue[] } | { ok: false; error: string };

/**
 * Набранное — в значения для вызова. Отказ — по-человечески: какой параметр и что
 * с ним не так. Числа, варианты и пределы сервер проверит ещё раз, но сказать
 * заранее — честнее, чем ждать отказа.
 */
export function parseCommand(typed: Typed, resolve: Resolve): Parsed {
  if (typed.options.some(isSub)) {
    const names = typed.options.map((option) => option.name).join(", ");
    return { ok: false, error: `Выберите подкоманду: ${names}` };
  }

  const given = pairs(typed);
  if (!given.ok) return given;

  const values: CommandValue[] = [];
  for (const option of typed.options) {
    const raw = given.values.get(option.name);
    if (raw === undefined || raw === "") {
      if (option.required) return { ok: false, error: `Нужен параметр «${option.name}»` };
      continue;
    }
    const value = valueOf(option, raw, resolve);
    if (value === null) return { ok: false, error: `«${option.name}»: ${expected(option)}` };
    values.push({ name: option.name, value });
  }

  // Подкоманды заворачиваются снаружи внутрь: значения — у самой глубокой.
  let wrapped = values;
  for (const name of [...typed.path].reverse()) wrapped = [{ name, options: wrapped }];
  return { ok: true, options: wrapped };
}

/**
 * «имя:значение» по тексту. Значение — до следующего «имя:» с известным именем,
 * так что пробелы в строке не мешают. Без единого имени весь текст — первому
 * строковому параметру, как «/echo привет».
 */
function pairs(typed: Typed): { ok: true; values: Map<string, string> } | { ok: false; error: string } {
  const values = new Map<string, string>();
  const rest = typed.rest.trim();
  const marks = [...rest.matchAll(/(?:^|\s)([^\s:]+):/g)]
    .map((match) => ({
      option: typed.options.find((option) => option.name === match[1].toLowerCase()),
      // Где начинается само «имя:» — пробел перед ним не в счёт.
      start: match.index + match[0].length - match[1].length - 1,
      end: match.index + match[0].length,
    }))
    .filter((mark) => mark.option);

  if (marks.length === 0) {
    if (!rest) return { ok: true, values };
    const first = typed.options[0];
    if (first && first.type === OPTION.text && typed.options.filter((option) => option.required).length <= 1) {
      values.set(first.name, rest);
      return { ok: true, values };
    }
    return { ok: false, error: `Параметры пишутся так: ${typed.options.map((option) => `${option.name}:`).join(" ")}` };
  }
  if (rest.slice(0, marks[0].start).trim()) {
    return { ok: false, error: `Непонятно, к чему «${rest.slice(0, marks[0].start).trim()}»` };
  }
  marks.forEach((mark, index) => {
    const until = index + 1 < marks.length ? marks[index + 1].start : rest.length;
    values.set(mark.option!.name, rest.slice(mark.end, until).trim());
  });
  return { ok: true, values };
}

const YES = new Set(["да", "yes", "true", "1", "вкл", "on"]);
const NO = new Set(["нет", "no", "false", "0", "выкл", "off"]);

function valueOf(option: CommandOption, raw: string, resolve: Resolve): string | number | boolean | null {
  if (option.choices?.length) {
    const wanted = raw.toLowerCase();
    const choice = option.choices.find(
      (item) => item.name.toLowerCase() === wanted || String(item.value).toLowerCase() === wanted,
    );
    return choice ? choice.value : null;
  }
  switch (option.type) {
    case OPTION.text:
      return within(option, raw.length) ? raw : null;
    case OPTION.whole: {
      if (!/^-?\d+$/.test(raw)) return null;
      const number = Number(raw);
      return within(option, number, true) ? number : null;
    }
    case OPTION.number: {
      const number = Number(raw.replace(",", "."));
      return Number.isFinite(number) && within(option, number, true) ? number : null;
    }
    case OPTION.yesNo: {
      const word = raw.toLowerCase();
      return YES.has(word) ? true : NO.has(word) ? false : null;
    }
    case OPTION.user:
      return person(raw, resolve);
    case OPTION.channel:
      return /^<#([0-9a-f-]{36})>$/i.exec(raw)?.[1] ?? null;
    case OPTION.role:
      return role(raw);
    case OPTION.mentionable:
      return person(raw, resolve) ?? role(raw);
    default:
      return null;
  }
}

const person = (raw: string, resolve: Resolve) => (raw.startsWith("@") ? resolve.person(raw.slice(1)) : null);
const role = (raw: string) => /^<@&([0-9a-f-]{36})>$/i.exec(raw)?.[1] ?? null;

/** Пределы: у строки — длина, у числа — значение. */
function within(option: CommandOption, value: number, numeric = false) {
  const min = numeric ? option.min_value : option.min_length;
  const max = numeric ? option.max_value : option.max_length;
  return (min === undefined || value >= min) && (max === undefined || value <= max);
}

/** Что ждали — в отказе. */
function expected(option: CommandOption): string {
  if (option.choices?.length) return `одно из: ${option.choices.map((choice) => choice.name).join(", ")}`;
  const bounds = (min?: number, max?: number) =>
    min !== undefined && max !== undefined
      ? ` от ${min} до ${max}`
      : min !== undefined
        ? ` от ${min}`
        : max !== undefined
          ? ` до ${max}`
          : "";
  switch (option.type) {
    case OPTION.text:
      return `текст${bounds(option.min_length, option.max_length)} знаков`;
    case OPTION.whole:
      return `целое число${bounds(option.min_value, option.max_value)}`;
    case OPTION.number:
      return `число${bounds(option.min_value, option.max_value)}`;
    case OPTION.yesNo:
      return "да или нет";
    case OPTION.file:
      return "файлы в командах пока не поддерживаются";
    default:
      return `нужен ${optionKind(option)} — выберите из подсказки`;
  }
}

/**
 * Набранное для автодополнения (тип 4): параметр, который сейчас пишут, — строкой
 * как есть и в фокусе, остальные — те, что уже годятся. `start` — где в тексте
 * начинается значение в фокусе: выбранный вариант встаёт туда. `null` — в фокусе
 * нет параметра с автодополнением.
 */
export function autocompleteOf(
  text: string,
  typed: Typed,
  resolve: Resolve,
): { option: CommandOption; values: CommandValue[]; start: number } | null {
  const marks = [...typed.rest.matchAll(/(?:^|\s)([^\s:]+):/g)]
    .map((match) => ({
      option: typed.options.find((option) => option.name === match[1].toLowerCase()),
      end: match.index + match[0].length,
    }))
    .filter((mark) => mark.option);
  const last = marks.at(-1);
  if (!last?.option?.autocomplete) return null;

  const values: CommandValue[] = [];
  marks.forEach((mark, index) => {
    const option = mark.option!;
    if (index === marks.length - 1) {
      values.push({ name: option.name, value: typed.rest.slice(last.end).trimStart(), focused: true });
      return;
    }
    const raw = typed.rest.slice(mark.end, marks[index + 1].end - marks[index + 1].option!.name.length - 1).trim();
    const value = raw ? valueOf(option, raw, resolve) : null;
    if (value !== null) values.push({ name: option.name, value });
  });

  let wrapped = values;
  for (const name of [...typed.path].reverse()) wrapped = [{ name, options: wrapped }];
  // Остаток — хвост текста: значение в фокусе начинается за «имя:» и пробелами.
  const restAt = text.length - typed.rest.length;
  const typing = typed.rest.slice(last.end);
  return { option: last.option, values: wrapped, start: restAt + last.end + (typing.length - typing.trimStart().length) };
}
